import React, { useEffect, useMemo, useState } from 'react'
import {
  createCustomContent,
  getApiErrorMessage,
  getCustomContents,
  getModelFans,
  getModels,
  prepareCustomContentForFansly,
  recordCustomContentSend,
  resetCustomContentDelivery,
  updateCustomContent,
} from '../../api'
import MediaViewer from '../../components/MediaViewer'
import { EmptyState, LoadingBlock, MetricCard, Section } from '../../components/v2/V2Primitives'
import { asList, modelTitle } from './v2Utils'

const DEFAULT_CONTEXT = `Use this context only when it naturally fits the conversation.\n\nBrief / story:\n\nDelivery notes:\n\nWhat the operator should avoid promising:\n`

const PAYMENT_OPTIONS = [
  ['unknown', 'Unknown'],
  ['pending', 'Pending'],
  ['paid_via_tip', 'Paid via tip'],
  ['paid_other', 'Paid another way'],
  ['not_required', 'Not required'],
  ['refunded', 'Refunded'],
]

function formatDate(value) {
  if (!value) return '—'
  try { return new Date(value).toLocaleString() } catch { return value }
}

export default function AdminV2Customs() {
  const [models, setModels] = useState([])
  const [modelId, setModelId] = useState('')
  const [fans, setFans] = useState([])
  const [fanId, setFanId] = useState('')
  const [fanSearch, setFanSearch] = useState('')
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingOrders, setLoadingOrders] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState('')
  const [draft, setDraft] = useState({
    title: 'The Answer Behind the Mask',
    payment_status: 'paid_via_tip',
    delivery_mode: 'manual_only',
    delivery_policy: 'when_relevant',
    delivery_guidance: '',
    scheduled_at: '',
    trigger_terms: '',
    explicitness_level: '0',
    body_parts: '',
    does_not_show: '',
    context: DEFAULT_CONTEXT,
    operator_notes: '',
    files: [],
  })
  const [viewerItem, setViewerItem] = useState(null)

  useEffect(() => {
    let alive = true
    getModels()
      .then((result) => {
        if (!alive) return
        const rows = asList(result)
        setModels(rows)
        if (rows[0]?.id != null) setModelId((current) => current || String(rows[0].id))
      })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load models')) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (!modelId) return
    let alive = true
    setFanId('')
    getModelFans(modelId, { search: fanSearch, limit: 200 })
      .then((result) => {
        if (!alive) return
        const rows = asList(result)
        setFans(rows)
        if (rows[0]?.id != null) setFanId((current) => current || String(rows[0].id))
      })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load fans')) })
    return () => { alive = false }
  }, [modelId, fanSearch])

  async function refreshOrders() {
    if (!modelId) return
    setLoadingOrders(true)
    try {
      const result = await getCustomContents({ modelId, limit: 100 })
      setOrders(asList(result))
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load custom content'))
    } finally {
      setLoadingOrders(false)
    }
  }

  useEffect(() => { refreshOrders() }, [modelId])

  const selectedModel = useMemo(() => models.find((item) => String(item.id) === String(modelId)), [models, modelId])
  const readyCount = orders.filter((item) => ['ready', 'scheduled'].includes(item.status)).length
  const sentCount = orders.filter((item) => ['manual_sent', 'auto_sent'].includes(item.delivery_status)).length

  function updateDraft(key, value) { setDraft((current) => ({ ...current, [key]: value })) }

  async function createOrder(event) {
    event.preventDefault()
    if (!modelId || !fanId) return
    setBusy('create')
    setError('')
    setNotice('')
    try {
      await createCustomContent({
        model_id: modelId,
        fan_id: fanId,
        title: draft.title,
        payment_status: draft.payment_status,
        delivery_mode: draft.delivery_mode,
        scheduled_at: draft.scheduled_at ? new Date(draft.scheduled_at).toISOString() : '',
        context: JSON.stringify(buildContextPayload(draft)),
        operator_notes: draft.operator_notes,
        files: draft.files,
      })
      setNotice('Custom saved as draft. Nothing was sent to the fan.')
      setDraft((current) => ({ ...current, files: [] }))
      event.target.reset()
      await refreshOrders()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create custom content order'))
    } finally {
      setBusy('')
    }
  }

  async function prepareFansly(order) {
    setBusy(`prepare-${order.id}`)
    setError('')
    setNotice('')
    try {
      await prepareCustomContentForFansly(order.id)
      setNotice(`Custom #${order.id} uploaded to Fansly and armed. No fan message was sent.`)
      await refreshOrders()
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to prepare custom for Fansly')) }
    finally { setBusy('') }
  }

  async function changeStatus(order, nextStatus) {
    setBusy(`status-${order.id}`)
    try {
      await updateCustomContent(order.id, { status: nextStatus })
      setNotice(`Order #${order.id} marked ${nextStatus}. No provider message was sent.`)
      await refreshOrders()
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to update order')) }
    finally { setBusy('') }
  }

  async function recordSend(order) {
    const uncertain = ['dispatching', 'delivery_unknown'].includes(order.delivery_status)
    if (!window.confirm(uncertain
      ? `After inspecting Fansly, confirm that “${order.title}” was delivered?`
      : `Confirm that an operator already sent “${order.title}” manually?`)) return
    setBusy(`send-${order.id}`)
    try {
      await recordCustomContentSend(order.id, { confirmed: true })
      setNotice(uncertain
        ? `Delivery confirmed for #${order.id}; the uncertain automatic attempt is reconciled.`
        : `Manual delivery recorded for #${order.id}. FanLTV did not send the media.`)
      await refreshOrders()
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to record manual delivery')) }
    finally { setBusy('') }
  }

  async function resetDelivery(order) {
    if (!window.confirm(`Did you inspect Fansly and confirm that “${order.title}” was NOT sent? This will allow one new automatic attempt.`)) return
    setBusy(`reset-${order.id}`)
    setError('')
    setNotice('')
    try {
      await resetCustomContentDelivery(order.id, { confirmedNotSent: true })
      setNotice(`Delivery attempt for #${order.id} reset after operator inspection.`)
      await refreshOrders()
    } catch (err) { setError(getApiErrorMessage(err, 'Failed to reset custom delivery')) }
    finally { setBusy('') }
  }

  if (loading) return <LoadingBlock label="Loading custom content workspace..." />

  return (
    <div className="v2-stack">
      <div className="v2-metrics-grid">
        <MetricCard label="Model" value={selectedModel ? modelTitle(selectedModel) : 'None'} detail="Custom content scope" />
        <MetricCard label="Orders" value={orders.length} detail="Drafts and active customs" />
        <MetricCard label="Ready / scheduled" value={readyCount} detail="Waiting for operator" tone={readyCount ? 'warn' : 'good'} />
        <MetricCard label="Delivered" value={sentCount} detail="Manual and automatic" />
      </div>

      <Section
        eyebrow="Customs"
        title="Upload a finished custom with its context"
        description="Uploading and preparing never sends a fan message. Manual-only waits for an operator; bot contextual can deliver an already-paid custom when the configured conversational moment arrives."
      >
        {error ? <div className="v2-error">{error}</div> : null}
        {notice ? <div className="v2-info">{notice}</div> : null}
        <form className="v2-form-grid" onSubmit={createOrder}>
          <label><span>Model</span><select value={modelId} onChange={(event) => setModelId(event.target.value)}>{models.map((item) => <option key={item.id} value={item.id}>{modelTitle(item)}</option>)}</select></label>
          <label><span>Find fan</span><input value={fanSearch} onChange={(event) => setFanSearch(event.target.value)} placeholder="name, username, external id" /></label>
          <label><span>Fan</span><select value={fanId} onChange={(event) => setFanId(event.target.value)}><option value="">Select fan</option>{fans.map((item) => <option key={item.id} value={item.id}>{item.name || item.username || item.external_id || `Fan #${item.id}`} (#{item.id})</option>)}</select></label>
          <label className="v2-form-wide"><span>Title</span><input value={draft.title} onChange={(event) => updateDraft('title', event.target.value)} maxLength={256} required /></label>
          <label><span>Payment</span><select value={draft.payment_status} onChange={(event) => updateDraft('payment_status', event.target.value)}>{PAYMENT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label><span>Delivery</span><select value={draft.delivery_mode} onChange={(event) => updateDraft('delivery_mode', event.target.value)}><option value="manual_only">Manual only</option><option value="bot_contextual">Bot contextual</option></select></label>
          {draft.delivery_mode === 'bot_contextual' ? <label><span>Bot timing</span><select value={draft.delivery_policy} onChange={(event) => updateDraft('delivery_policy', event.target.value)}><option value="when_relevant">Send when contextually relevant</option><option value="explicit_request">Only after a direct fan request</option></select></label> : null}
          <label><span>Planned viewing (optional)</span><input type="datetime-local" value={draft.scheduled_at} onChange={(event) => updateDraft('scheduled_at', event.target.value)} /></label>
          <label className="v2-form-wide"><span>Finished media, max 100 MB each</span><input type="file" multiple required accept="video/*,image/*,.heic,.heif" onChange={(event) => updateDraft('files', Array.from(event.target.files || []))} /><small className="v2-muted">Up to 10 files and 500 MB total. HEIC/HEIF: max 50 MB, converted to JPEG for viewing and delivery; originals retained.</small></label>
          <label className="v2-form-wide"><span>Contextual cues {draft.delivery_mode === 'bot_contextual' ? '(required)' : '(optional)'}</span><input required={draft.delivery_mode === 'bot_contextual'} value={draft.trigger_terms} onChange={(event) => updateDraft('trigger_terms', event.target.value)} placeholder="e.g. behind the mask, unfinished kiss, pomegranate fantasy" /><small className="v2-muted">Use distinctive phrases of at least two words. In when-relevant mode they guide semantic matching; in direct-request mode one must appear in the fan's request.</small></label>
          {draft.delivery_mode === 'bot_contextual' && draft.delivery_policy === 'when_relevant' ? <label className="v2-form-wide"><span>When should the bot send it? (required)</span><textarea rows={3} required minLength={20} maxLength={2500} value={draft.delivery_guidance} onChange={(event) => updateDraft('delivery_guidance', event.target.value)} placeholder="Example: Send during the planned private date after he arrives and the conversation returns to the mask, pomegranate, unfinished kiss, or what is behind the persona. Do not send during an unrelated topic or if he asks to wait." /><small className="v2-muted">Describe the conversational moment, not exact wording. The semantic gate waits when uncertain and never bypasses payment, safety, schedule, or a fan deferral.</small></label> : null}
          <label><span>Explicitness 0–100</span><input type="number" min="0" max="100" value={draft.explicitness_level} onChange={(event) => updateDraft('explicitness_level', event.target.value)} /></label>
          <label><span>Shown attributes</span><input value={draft.body_parts} onChange={(event) => updateDraft('body_parts', event.target.value)} placeholder="e.g. corset, stockings" /></label>
          <label><span>Does not show</span><input value={draft.does_not_show} onChange={(event) => updateDraft('does_not_show', event.target.value)} placeholder="e.g. face, nudity" /></label>
          <label className="v2-form-wide"><span>Context for the bot and operator</span><textarea rows={12} value={draft.context} onChange={(event) => updateDraft('context', event.target.value)} placeholder="Brief, callbacks, scenes, boundaries, delivery notes..." /></label>
          <label className="v2-form-wide"><span>Operator notes</span><textarea rows={3} value={draft.operator_notes} onChange={(event) => updateDraft('operator_notes', event.target.value)} /></label>
          <div className="v2-form-wide v2-inline-actions"><span className="v2-muted">Delivery mode: <strong>{draft.delivery_mode === 'bot_contextual' ? (draft.delivery_policy === 'when_relevant' ? 'bot chooses the relevant moment' : 'after a direct fan request') : 'manual only'}</strong></span><button type="submit" className="v2-btn v2-btn-primary" disabled={busy === 'create' || !modelId || !fanId}>{busy === 'create' ? 'Saving...' : 'Save custom draft'}</button></div>
        </form>
      </Section>

      <Section eyebrow="Operator queue" title="Custom production and delivery" description="Prepare media on Fansly without messaging the fan, then keep manual control or arm contextual bot delivery.">
        {loadingOrders ? <LoadingBlock label="Loading custom orders..." /> : orders.length ? <div className="v2-stack">{orders.map((order) => <OrderCard key={order.id} order={order} busy={busy} onStatus={changeStatus} onPrepare={prepareFansly} onOpen={setViewerItem} onRecordSend={recordSend} onResetDelivery={resetDelivery} />)}</div> : <EmptyState title="No custom content orders" description="Upload finished media above to create the first custom order." />}
      </Section>
      <MediaViewer
        open={Boolean(viewerItem)}
        onClose={() => setViewerItem(null)}
        modelId={viewerItem?.modelId}
        item={viewerItem?.item}
      />
    </div>
  )
}

function OrderCard({ order, busy, onStatus, onPrepare, onOpen, onRecordSend, onResetDelivery }) {
  const contextPreview = typeof order.context?.brief === 'string' ? order.context.brief : (order.context?.summary || '')
  const needsReview = ['dispatching', 'delivery_unknown'].includes(order.delivery_status)
  const hasUnresolvedAttempt = ['reserved', 'dispatching', 'delivery_unknown'].includes(order.delivery_status)
  return <article className="v2-card">
    <div className="v2-card-header"><div><p className="v2-kicker">Custom #{order.id}</p><h3>{order.title}</h3><p>{order.fan_label} · {order.model_label}</p></div><span className={`v2-status v2-status-${order.status}`}>{order.status}</span></div>
    <div className="v2-card-grid"><div><strong>Payment</strong><span>{order.payment_status}</span></div><div><strong>Delivery</strong><span>{order.delivery_mode}{order.context?.delivery_policy ? ` / ${order.context.delivery_policy}` : ''} · {order.delivery_status}</span></div><div><strong>Planned</strong><span>{formatDate(order.scheduled_at)}</span></div><div><strong>Created</strong><span>{formatDate(order.created_at)}</span></div></div>
    {contextPreview ? <p className="v2-muted">{String(contextPreview).slice(0, 420)}{String(contextPreview).length > 420 ? '…' : ''}</p> : null}
    {needsReview ? <div className="v2-error"><strong>Manual review required.</strong> The provider attempt may have succeeded. Automation is stopped; inspect Fansly before choosing either action below.</div> : null}
    {order.delivery_status === 'reserved' ? <div className="v2-info"><strong>Reserved by a bot graph.</strong> Only that graph token may continue. If it is no longer running, inspect the chat before resetting the reservation.</div> : null}
    <div className="v2-inline-actions">{order.media?.length ? order.media.map((item) => <button key={item.id} type="button" className="v2-btn v2-btn-secondary" onClick={() => onOpen({ modelId: order.model_id, item: { id: item.id, type: item.media_type, source: 'local', media_path: item.file_url || true } })}>Open {item.label || item.original_filename || `media #${item.id}`}</button>) : <span className="v2-muted">No media uploaded</span>}{order.status === 'draft' && order.delivery_mode === 'manual_only' ? <button type="button" className="v2-btn v2-btn-secondary" disabled={busy === `status-${order.id}`} onClick={() => onStatus(order, 'ready')}>Mark ready</button> : null}{order.status === 'draft' && order.delivery_mode === 'bot_contextual' ? <button type="button" className="v2-btn v2-btn-primary" disabled={busy === `prepare-${order.id}`} onClick={() => onPrepare(order)}>{busy === `prepare-${order.id}` ? 'Preparing...' : 'Upload to Fansly & arm (no fan message)'}</button> : null}{['ready', 'scheduled'].includes(order.status) && (order.delivery_mode === 'manual_only' || needsReview) ? <button type="button" className="v2-btn v2-btn-secondary" disabled={busy === `send-${order.id}`} onClick={() => onRecordSend(order)}>{needsReview ? 'Confirm delivered' : 'Record manual send'}</button> : null}{hasUnresolvedAttempt ? <button type="button" className="v2-btn v2-btn-ghost" disabled={busy === `reset-${order.id}`} onClick={() => onResetDelivery(order)}>Confirm not sent & reset</button> : null}{!hasUnresolvedAttempt && order.status !== 'archived' && order.status !== 'sent' ? <button type="button" className="v2-btn v2-btn-ghost" disabled={busy === `status-${order.id}`} onClick={() => onStatus(order, 'archived')}>Archive</button> : null}</div>
  </article>
}

function buildContextPayload(draft) {
  const raw = draft.context
  let payload = {}
  try {
    const parsed = JSON.parse(String(raw || ''))
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) payload = parsed
  } catch {
    payload = { brief: raw || '' }
  }
  const terms = String(draft.trigger_terms || '').split(',').map((item) => item.trim()).filter(Boolean)
  if (terms.length) payload.trigger_terms = terms
  payload.delivery_policy = draft.delivery_mode === 'bot_contextual' ? draft.delivery_policy : 'explicit_request'
  if (draft.delivery_mode === 'bot_contextual' && draft.delivery_policy === 'when_relevant') payload.delivery_guidance = String(draft.delivery_guidance || '').trim()
  payload.explicitness_level = Math.max(0, Math.min(100, Number(draft.explicitness_level) || 0))
  payload.body_parts = String(draft.body_parts || '').split(',').map((item) => item.trim()).filter(Boolean)
  payload.does_not_show = String(draft.does_not_show || '').split(',').map((item) => item.trim()).filter(Boolean)
  return payload
}
